import { useEffect, useMemo, useState } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import type { TrackingResponse } from '@mrfw/shared/api';
import type { TrackingEvent } from '@mrfw/shared/events';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) ?? 'http://localhost:3000';
const MAPS_KEY = (import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string) ?? '';

interface Props {
  orderNumber: string;
  token: string;
}

export function TrackingPage({ orderNumber, token }: Props) {
  const [snapshot, setSnapshot] = useState<TrackingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [livePosition, setLivePosition] = useState<{ lat: number; lng: number } | null>(null);

  const { isLoaded: mapsLoaded, loadError } = useLoadScript({
    googleMapsApiKey: MAPS_KEY,
  });

  useEffect(() => {
    let cancelled = false;
    const url = `${API_BASE}/track/${encodeURIComponent(orderNumber)}?t=${encodeURIComponent(token)}`;
    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((body) => {
        if (cancelled) return;
        if (!body.ok) {
          setError(body.error ?? 'Could not load tracking');
        } else {
          setSnapshot(body.data as TrackingResponse);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Network error');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [orderNumber, token]);

  // Open WS for live updates.
  useEffect(() => {
    const wsUrl = `${API_BASE.replace(/^http/, 'ws')}/ws/track/${encodeURIComponent(
      orderNumber
    )}?t=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    ws.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data as string) as TrackingEvent;
        if (event.type === 'position') {
          setLivePosition({ lat: event.position.lat, lng: event.position.lng });
        } else if (event.type === 'stop_status') {
          setSnapshot((prev) => (prev ? { ...prev, status: event.status } : prev));
        } else if (event.type === 'eta') {
          setSnapshot((prev) =>
            prev
              ? {
                  ...prev,
                  eta_seconds: event.eta_seconds,
                  is_driver_on_prior_stop: event.is_driver_on_prior_stop,
                  current_prior_stop_order_number: event.current_prior_stop_order_number,
                }
              : prev
          );
        }
      } catch {
        // ignore malformed
      }
    };
    return () => ws.close();
  }, [orderNumber, token]);

  const driverPosition = useMemo(
    () => livePosition ?? (snapshot?.current_position ? { lat: snapshot.current_position.lat, lng: snapshot.current_position.lng } : null),
    [livePosition, snapshot]
  );

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center text-gray-500">Loading…</div>
    );
  }
  if (error || !snapshot) {
    return (
      <div className="min-h-full flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-xl font-semibold text-brand-700 mb-2">We couldn't find that order</h1>
          <p className="text-gray-600 text-sm">
            The link may have expired. Please check your WhatsApp message or contact support.
          </p>
        </div>
      </div>
    );
  }

  const etaText = (() => {
    if (snapshot.eta_seconds) {
      return `${Math.max(1, Math.round(snapshot.eta_seconds / 60))} min`;
    }
    if (snapshot.status === 'delivered') return 'Delivered';
    if (snapshot.status === 'failed') return '—';
    if (!snapshot.driver) return 'Awaiting driver assignment';
    if (!snapshot.current_position) return 'Awaiting trip start';
    if (!snapshot.destination) return 'Address being verified';
    return 'Updating…';
  })();

  return (
    <div className="min-h-full flex flex-col bg-gray-50">
      <header className="bg-brand text-white p-4 shadow-md">
        <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">Mr Fresh Wheels</div>
        <div className="text-xs opacity-80">Order</div>
        <div className="text-lg font-semibold">{snapshot.order_number}</div>
        <div className="text-sm mt-1">Hi {snapshot.customer_first_name} 👋</div>
      </header>

      {snapshot.is_driver_on_prior_stop && snapshot.current_prior_stop_order_number && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm">
          Your driver is currently on Order #{snapshot.current_prior_stop_order_number}. Your delivery starts after that.
        </div>
      )}

      <div className="p-4">
        <div className="bg-white rounded-lg shadow p-4 mb-3">
          <div className="text-xs uppercase text-gray-500">Status</div>
          <div className="text-base font-medium capitalize">{snapshot.status.replace(/_/g, ' ')}</div>
          <div className="text-xs uppercase text-gray-500 mt-3">ETA</div>
          <div className="text-base font-medium">{etaText}</div>
        </div>

        {snapshot.driver && (
          <div className="bg-white rounded-lg shadow p-4 mb-3 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase text-gray-500">Delivery partner</div>
              <div className="text-base font-medium">{snapshot.driver.name}</div>
              {snapshot.vehicle && (
                <div className="text-xs text-gray-500">{snapshot.vehicle.registration_number}</div>
              )}
            </div>
            {snapshot.driver.phone && (
              <a
                className="text-brand-700 underline text-sm"
                href={`tel:${snapshot.driver.phone}`}
              >
                Call
              </a>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden" style={{ height: 360 }}>
          {loadError ? (
            <div className="p-4 text-sm text-red-600">Map failed to load.</div>
          ) : !mapsLoaded || !MAPS_KEY ? (
            <div className="p-4 text-sm text-gray-500">
              {!MAPS_KEY ? 'Map will appear once the Maps key is configured.' : 'Loading map…'}
            </div>
          ) : driverPosition ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={driverPosition}
              zoom={15}
              options={{ disableDefaultUI: true, gestureHandling: 'greedy' }}
            >
              <Marker position={driverPosition} />
              {snapshot.destination && <Marker position={snapshot.destination} />}
            </GoogleMap>
          ) : (
            <div className="p-4 text-sm text-gray-500">Waiting for driver to start the trip.</div>
          )}
        </div>
      </div>

      <footer className="text-center text-xs text-gray-500 py-3">Powered by Mr Fresh Wheels</footer>
    </div>
  );
}
